/* Avalon Archives — theme behaviour.
 * Loaded with `defer` from the layout, once.
 *
 * Owns exactly four things:
 *   1. the mobile drawer (open/close, scrim, Esc, focus trap, focus restore)
 *   2. the membership state gate — nothing purchasable exists unless the global
 *      `membership_state` is "open"
 *   3. the membership summary, filled from Shopify selling-plan data (never typed)
 *   4. the Home experience layer (progressive reveals and fine-pointer depth)
 *
 * The FAQ is native <details>/<summary> and needs no script. There is one plan,
 * so there is no selector, no toggle, and no mode switch here.
 */

(function () {
  "use strict";

  var FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(",");

  /* -----------------------------------------------------------------------
   * 1. Mobile drawer
   * -------------------------------------------------------------------- */

  function initDrawer() {
    var toggle = document.querySelector("[data-avalon-drawer-toggle]");
    var drawer = document.querySelector("[data-avalon-drawer]");
    var scrim = document.querySelector("[data-avalon-drawer-scrim]");

    if (!toggle || !drawer || !scrim) {
      return;
    }

    var lastFocused = null;

    function focusables() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
    }

    function onKeydown(event) {
      if (event.key === "Escape") {
        close();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      var items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.setAttribute("data-open", "true");
      scrim.setAttribute("data-open", "true");
      drawer.removeAttribute("hidden");
      toggle.setAttribute("aria-expanded", "true");
      document.addEventListener("keydown", onKeydown);

      var items = focusables();
      if (items.length > 0) {
        items[0].focus();
      }
    }

    function close() {
      drawer.setAttribute("data-open", "false");
      scrim.setAttribute("data-open", "false");
      toggle.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", onKeydown);

      // Hide from assistive tech only after the transition, so focus never lands
      // in a hidden subtree mid-animation.
      window.setTimeout(function () {
        if (drawer.getAttribute("data-open") !== "true") {
          drawer.setAttribute("hidden", "hidden");
        }
      }, 200);

      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    toggle.addEventListener("click", function () {
      if (drawer.getAttribute("data-open") === "true") {
        close();
      } else {
        open();
      }
    });

    scrim.addEventListener("click", close);
    document.addEventListener("avalon:before-navigate", function () {
      if (drawer.getAttribute("data-open") === "true") close();
    });

    var dismiss = drawer.querySelector("[data-avalon-drawer-close]");
    if (dismiss) {
      dismiss.addEventListener("click", close);
    }
  }

  /* -----------------------------------------------------------------------
   * 2. Membership state gate + 3. summary
   *
   * The Liquid never renders a product form unless the state is "open", so this
   * is a second, defensive gate: if the state is anything else, any stray
   * purchase control is removed rather than merely hidden.
   * -------------------------------------------------------------------- */

  function initMembership() {
    var root = document.querySelector("[data-avalon-membership]");
    if (!root) {
      return;
    }

    var state = root.getAttribute("data-membership-state");

    if (state !== "open") {
      Array.prototype.forEach.call(
        root.querySelectorAll("form[action*='/cart/add']"),
        function (form) {
          form.parentNode.removeChild(form);
        }
      );
      return;
    }

    // One monthly plan. Its id and rendered price come from Shopify via
    // data attributes the section wrote from live selling-plan data — this file
    // never computes, formats, or invents a price.
    var planInput = root.querySelector("[data-avalon-selling-plan-input]");
    var planId = root.getAttribute("data-selling-plan-id");

    if (planInput && planId) {
      planInput.value = planId;
    }

    var priceSource = root.querySelector("[data-avalon-plan-price]");
    var dueToday = root.querySelector("[data-avalon-due-today]");

    if (priceSource && dueToday) {
      dueToday.textContent = priceSource.textContent.trim();
    }
  }

  /* -----------------------------------------------------------------------
   * 4. Home experience layer
   *
   * Content stays visible without JavaScript. Once this initializer runs, it
   * progressively reveals the editorial stages as they enter the viewport.
   * Fine-pointer depth is deliberately tiny and never runs for reduced motion.
   * -------------------------------------------------------------------- */

  function initExperience() {
    var root = document.querySelector('[data-avalon-route="home"]');
    if (!root) {
      return;
    }

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    var selectors = [
      ".avalon-reference-home__hero-copy",
      ".avalon-reference-home__hero-collage",
      ".avalon-reference-home__story",
      ".avalon-reference-home__inside > header",
      ".avalon-reference-home__piece",
      ".avalon-reference-home__value",
      ".avalon-reference-home__preview",
      ".avalon-reference-home__membership-copy"
    ].join(",");
    var revealItems = Array.prototype.slice.call(root.querySelectorAll(selectors));

    root.classList.add("avalon-experience-ready");
    revealItems.forEach(function (item, index) {
      item.classList.add("avalon-reveal");
      item.style.setProperty("--avalon-reveal-delay", String((index % 3) * 90) + "ms");
    });

    if (reduceMotion.matches || !("IntersectionObserver" in window)) {
      revealItems.forEach(function (item) {
        item.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    revealItems.forEach(function (item) {
      observer.observe(item);
    });

    var hero = root.querySelector(".avalon-reference-home__hero");
    var collage = root.querySelector(".avalon-reference-home__hero-collage");
    var closingStage = root.querySelector(".avalon-reference-home__closing-stage");
    var depthFrame = null;

    function paintDepth() {
      depthFrame = null;

      if (!window.matchMedia("(min-width: 750px)").matches) {
        root.style.setProperty("--avalon-landscape-depth", "0px");
        root.style.setProperty("--avalon-hero-depth", "0px");
        root.style.setProperty("--avalon-night-depth", "0px");
        return;
      }

      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (hero) {
        var heroRect = hero.getBoundingClientRect();
        var heroProgress = Math.max(
          0,
          Math.min(1, (viewportHeight - heroRect.top) / (viewportHeight + heroRect.height))
        );
        var heroCentered = heroProgress - 0.5;
        root.style.setProperty(
          "--avalon-landscape-depth",
          (heroCentered * 24).toFixed(2) + "px"
        );
        root.style.setProperty(
          "--avalon-hero-depth",
          (heroCentered * -14).toFixed(2) + "px"
        );
      }

      if (closingStage) {
        var nightRect = closingStage.getBoundingClientRect();
        var nightProgress = Math.max(
          0,
          Math.min(1, (viewportHeight - nightRect.top) / (viewportHeight + nightRect.height))
        );
        root.style.setProperty(
          "--avalon-night-depth",
          ((nightProgress - 0.5) * 22).toFixed(2) + "px"
        );
      }
    }

    function queueDepth() {
      if (!depthFrame) {
        depthFrame = window.requestAnimationFrame(paintDepth);
      }
    }

    window.addEventListener("scroll", queueDepth, { passive: true });
    window.addEventListener("resize", queueDepth);
    paintDepth();

    function cleanupExperience() {
      observer.disconnect();
      window.removeEventListener("scroll", queueDepth);
      window.removeEventListener("resize", queueDepth);
      if (depthFrame) window.cancelAnimationFrame(depthFrame);
      if (frame) window.cancelAnimationFrame(frame);
    }

    if (!window.matchMedia("(pointer: fine)").matches || !hero || !collage) {
      return cleanupExperience;
    }

    var frame = null;
    hero.addEventListener("pointermove", function (event) {
      var clientX = event.clientX;
      var clientY = event.clientY;
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      frame = window.requestAnimationFrame(function () {
        var rect = hero.getBoundingClientRect();
        var x = ((clientX - rect.left) / rect.width - 0.5) * 8;
        var y = ((clientY - rect.top) / rect.height - 0.5) * 6;
        collage.style.setProperty("--avalon-pointer-x", x.toFixed(2) + "px");
        collage.style.setProperty("--avalon-pointer-y", y.toFixed(2) + "px");
      });
    });

    hero.addEventListener("pointerleave", function () {
      collage.style.setProperty("--avalon-pointer-x", "0px");
      collage.style.setProperty("--avalon-pointer-y", "0px");
    });
    return cleanupExperience;
  }

  function initChapterRail() {
    var root = document.querySelector('[data-avalon-route="home"]');
    if (!root) {
      return;
    }

    var links = Array.prototype.slice.call(
      root.querySelectorAll("[data-avalon-chapter-link]")
    );
    var chapters = Array.prototype.slice.call(
      root.querySelectorAll("[data-avalon-chapter]")
    );
    if (!links.length || !chapters.length) {
      return;
    }

    function setActive(chapterName) {
      links.forEach(function (link) {
        if (link.getAttribute("data-avalon-chapter-link") === chapterName) {
          link.setAttribute("aria-current", "location");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    }

    links.forEach(function (link) {
      link.addEventListener("click", function (event) {
        var target = document.querySelector(link.getAttribute("href"));
        if (!target) {
          return;
        }
        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start"
        });
        setActive(link.getAttribute("data-avalon-chapter-link"));
      });
    });

    if (!("IntersectionObserver" in window)) {
      return;
    }

    var chapterObserver = new IntersectionObserver(
      function (entries) {
        var visible = entries
          .filter(function (entry) { return entry.isIntersecting; })
          .sort(function (left, right) { return right.intersectionRatio - left.intersectionRatio; });
        if (visible.length) {
          setActive(visible[0].target.getAttribute("data-avalon-chapter"));
        }
      },
      { threshold: 0, rootMargin: "-38% 0px -61% 0px" }
    );

    chapters.forEach(function (chapter) {
      chapterObserver.observe(chapter);
    });
    return function () { chapterObserver.disconnect(); };
  }

  var pageCleanups = [];
  function initPage() {
    pageCleanups.forEach(function (cleanup) { if (cleanup) cleanup(); });
    initMembership();
    pageCleanups = [initExperience(), initChapterRail()];
  }

  function init() {
    initDrawer();
    initPage();
    document.addEventListener("avalon:navigated", initPage);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
